import { Icon } from '@/core/icon';
import { Skeleton, TypewriterEffectSmooth, Typography } from '@/core/ui';
import { cn } from '@/lib';
import type { ICategory } from '@/types';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface Props extends ICategory, Omit<React.ComponentProps<'div'>, 'id' | 'title'> {
  route: string;
  titleClass?: string;
  loaded: boolean;
}

export const HomeSectionLoading = () => {
  return (
    <div className='flex flex-col justify-center items-center space-y-5'>
      <Skeleton className='w-52 h-44 rounded-md' />
      <Skeleton className='h-12 w-72' />
      <Skeleton className='h-11 w-96' />

      <div className='pt-12'>
        <Skeleton className='bg-danger rounded-full h-[96px] w-[174px]' />
      </div>
    </div>
  );
};

export const HomeSection = ({ loaded, logo, titleClass, className, title, subTitle, route }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const animation = useMemo(() => {
    if (loaded) {
      return { initial: {}, animate: {} };
    }
    return { initial: { opacity: 0 }, animate: { opacity: 1 } };
  }, [loaded]);

  const handleClick = () => {
    navigate(route);
  };

  return (
    <div className={cn('flex flex-col justify-center items-center space-y-5', className)}>
      <motion.img
        viewport={{ once: true }}
        initial={animation.initial}
        animate={animation.animate}
        transition={{ duration: 1, delay: 0.1 }}
        alt={logo.caption || ''}
        className=''
        src={logo.path}
      />
      {/* <TypewriterEffectSmooth
        transition={{ delay: 0.4 }}
        className='max-w-[500px] line-clamp-2'
        words={title.split(' ').map((w) => ({
          text: w,
          className: 'font-bold text-[60px] leading-[57px] tracking-[4.2px] text-center ',
        }))}
        // className='my-0 ml-12'
      /> */}
      <motion.div
        viewport={{ once: true }}
        initial={animation.initial}
        animate={animation.animate}
        transition={{ duration: 2, delay: 0.3, ease: 'easeInOut' }}
        className={cn('block text-center', titleClass)}
      >
        <Typography
          className={cn('font-bold text-[60px] leading-[57px] max-w-[500px] tracking-[4.2px] text-center')}
        >
          {title}
        </Typography>
      </motion.div>
      {subTitle &&
        (loaded ? (
          <Typography className='text-[40px] leading-[40px] tracking-[6px] font-light'>{subTitle}</Typography>
        ) : (
          <TypewriterEffectSmooth
            transition={{ delay: 1 }}
            words={subTitle.split(' ').map((w) => ({
              text: w,
              className: 'text-[40px] leading-[40px] tracking-[6px] font-light',
            }))}
          />
        ))}

      <div className={cn({ 'pt-12': subTitle, 'pt-16': !subTitle })}>
        <button
          type='button'
          onClick={handleClick}
          className='text-chalk flex items-center justify-center space-x-[10px] bg-danger rounded-full h-[96px] w-[174px]'
        >
          <Typography className='pt-1 text-chalk text-3xl leading-[28.5px] tracking-normal uppercase'>
            {t('home.see')}
          </Typography>
          <Icon name='CHEVRON_RIGHT' />
        </button>
      </div>
    </div>
  );
};
